import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Select, Empty } from "@/components/Kit";
import { Save, Printer } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PERIODS = [
  { period: 1, start: "09:30", end: "10:15" }, { period: 2, start: "10:15", end: "11:00" }, { period: 3, start: "11:15", end: "12:00" },
  { period: 4, start: "12:00", end: "12:45" }, { period: 5, start: "13:30", end: "14:15" }, { period: 6, start: "14:15", end: "15:00" }, { period: 7, start: "15:10", end: "15:55" },
];
const blank = () => Object.fromEntries(DAYS.map((d) => [d, PERIODS.map((p) => ({ ...p, subject: "", teacher: "" }))]));

export default function TimetableTab() {
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("A");
  const [grid, setGrid] = useState(blank());
  const { data: classes = [] } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/school/academics/classes")).data });
  const { data: subjects = [] } = useQuery({ queryKey: ["subjects"], queryFn: async () => (await api.get("/school/academics/subjects")).data });
  const sections = classes.find((c) => c.name === cls)?.sections || ["A"];

  useEffect(() => {
    if (!cls) return;
    api.get("/school/academics/timetable", { params: { class_name: cls, section: sec } }).then(({ data }) => {
      const g = blank();
      DAYS.forEach((d) => { (data.days[d] || []).forEach((p, i) => { if (g[d][i]) g[d][i] = { ...g[d][i], ...p }; }); });
      setGrid(g);
    });
  }, [cls, sec]);

  const save = useMutation({
    mutationFn: async () => Promise.all(DAYS.map((d) => api.put("/school/academics/timetable", { class_name: cls, section: sec, day: d, periods: grid[d] }))),
    onSuccess: () => toast.success(`Timetable saved for ${cls}-${sec}`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const set = (d, i, k, v) => setGrid({ ...grid, [d]: grid[d].map((p, j) => (j === i ? { ...p, [k]: v } : p)) });

  return (
    <div data-testid="timetable-tab">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40"><Select label="Class" value={cls} onChange={(e) => { setCls(e.target.value); setSec("A"); }} data-testid="tt-class"><option value="">Select</option>{classes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</Select></div>
        <div className="w-28"><Select label="Section" value={sec} onChange={(e) => setSec(e.target.value)} data-testid="tt-section">{sections.map((s) => <option key={s}>{s}</option>)}</Select></div>
        <div className="ml-auto flex gap-2">
          <button className="ep-btn ep-btn-ghost" onClick={() => window.print()} disabled={!cls} data-testid="tt-print-btn"><Printer size={15} /> Print</button>
          <button className="ep-btn" onClick={() => save.mutate()} disabled={!cls || save.isPending} data-testid="tt-save-btn"><Save size={15} /> {save.isPending ? "Saving…" : "Save Timetable"}</button>
        </div>
      </div>
      {!cls ? <div className="ep-card"><Empty>Select a class & section to build the weekly timetable</Empty></div> : (
        <div className="ep-card overflow-x-auto print-area">
          <table className="w-full text-xs" data-testid="tt-grid">
            <thead><tr className="border-b border-slate-800 text-left uppercase tracking-wider text-slate-500">
              <th className="px-3 py-3">Period</th>{DAYS.map((d) => <th key={d} className="px-3 py-3">{d}</th>)}
            </tr></thead>
            <tbody>
              {PERIODS.map((p, i) => (
                <tr key={p.period} className="border-b border-slate-800/50">
                  <td className="px-3 py-2 align-top"><p className="font-semibold text-white">P{p.period}</p><p className="font-mono text-[10px] text-slate-500">{p.start}-{p.end}</p></td>
                  {DAYS.map((d) => (
                    <td key={d} className="px-1.5 py-1.5 align-top">
                      <select className="ep-input !px-2 !py-1.5 text-xs" value={grid[d][i].subject} onChange={(e) => set(d, i, "subject", e.target.value)} data-testid={`tt-${d}-${p.period}-subject`}>
                        <option value="">—</option>
                        {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                        <option value="Games">Games</option><option value="Library">Library</option>
                      </select>
                      <input className="ep-input mt-1 !px-2 !py-1 text-[11px]" placeholder="Teacher" value={grid[d][i].teacher} onChange={(e) => set(d, i, "teacher", e.target.value)} data-testid={`tt-${d}-${p.period}-teacher`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
